/*
 * The AI chats module's own slice of the client model (S13 §1): list facts only. Core hands each module
 * one opaque entry under `model.modules[<id>]`, and this is what AI chats keeps there — every chat the
 * reader owns as its row, the titles beside them, the reader's read state per chat, the newest usage
 * of every Claude login the reader has heard of, the worker machine's state word and the new-chat
 * form's options (the reader's own logins among them). It is what the rail, the start page's lists,
 * the unread dots and the quick switcher read, from the first paint.
 *
 * **Logins.** Each member connects their own Claude logins, and every request runs on its sender's
 * (S17). The reader's own logins come with the setup (`SetupView.myLogins`); whose login a chat runs on
 * comes with the chat (`ChatView.runsOn`). What changes between reads is a login's usage — at a limit,
 * refused by Claude, its plan's windows — which `aichat.login.changed` brings for every login by its
 * opaque id, never with an owner or a label. The slice keeps the newest of each, and a reader of a
 * login line asks it for the newer of the two (`newestUsage`).
 *
 * **Nothing a person or Claude wrote is in it but the titles**: an open chat's items and its live
 * picture live in the page's own stores (`./chatSession.ts`, `../live/liveStore.ts`), which come and
 * go with the page. This slice is structure-cloned to disk whenever it changes identity, and a
 * conversation streaming four pictures a second through it would be written to disk four times a
 * second.
 *
 * It is filled by the bootstrap's `modules.aichat` entry, which the server sends only to a reader the
 * module admits (a team member, inside `members_group` when one is set): its presence is what
 * `admitted` records, and what the rail's AI Chats row stands on.
 */

import type { Model } from "../../../core/store/model.ts"
import { defineModuleSlice } from "../../../core/store/moduleSlice.ts"
import type {
	AichatBootstrap,
	ChatRow,
	LoginUsage,
	MachineLine,
	PreviewLine,
	ReaderState,
	SetupView,
} from "../wire/aichat.gen.ts"

export const AICHAT_MODULE_ID = "aichat"

export interface AichatSlice {
	/** The server sent this reader the module's bootstrap entry: the module admits them. */
	admitted: boolean
	/** Every chat the reader owns that the lists know of, by chat id; the newest row by `rev`. */
	chats: Record<number, ChatRow>
	/** The titles of those chats. A title never rides an event; it is re-read when `titleRev` moves. */
	titles: Record<number, string>
	/** The `titleRev` each title in `titles` was read at. */
	titleRevs: Record<number, number>
	/** The reader's own read state per chat; the newest by `version`. */
	readers: Record<number, ReaderState>
	/** The newest usage of each Claude login the reader heard of, by the login's id. */
	loginUsage: Record<string, LoginUsage>
	/** The worker machine's state word; null before the bootstrap. */
	machine: MachineLine | null
	/** Every option and default of the new-chat form and the settings panel. */
	setup: SetupView | null
	/** The bootstrap held fewer chats than the reader owns; the start page pages the rest. */
	more: boolean
}

export function emptyAichatSlice(): AichatSlice {
	return {
		admitted: false,
		chats: {},
		titles: {},
		titleRevs: {},
		readers: {},
		loginUsage: {},
		machine: null,
		setup: null,
		more: false,
	}
}

/**
 * The plumbing core gives every module's slice (`core/store/moduleSlice.ts`): the slice out of a
 * model, the model with it replaced, and the slice read back off the disk.
 */
const AICHAT_SLICE = defineModuleSlice(AICHAT_MODULE_ID, emptyAichatSlice)

/** This module's slice; one shared empty slice until the bootstrap has written it. */
export const aichatSlice = AICHAT_SLICE.of

/** The model with this module's slice replaced, keeping the identity of everything else. */
export const withAichatSlice = AICHAT_SLICE.with

/** Whether the module admits the reader: what the rail's AI Chats row asks. */
export function aichatAdmits(model: Model): boolean {
	return aichatSlice(model).admitted
}

/** The row of one chat by its key, or undefined when the reader owns no such chat the slice knows. */
export function chatRowByKey(model: Model, key: string): ChatRow | undefined {
	return Object.values(aichatSlice(model).chats).find((row) => row.key === key)
}

/** The slice as the bootstrap describes it: the bootstrap's rows replace whatever the slice held. */
export function sliceFromBootstrap(boot: AichatBootstrap): AichatSlice {
	const chats: Record<number, ChatRow> = {}
	const titleRevs: Record<number, number> = {}
	for (const row of boot.chats) {
		chats[row.chatId] = row
		titleRevs[row.chatId] = row.titleRev
	}
	const readers: Record<number, ReaderState> = {}
	for (const reader of boot.readers) readers[reader.chatId] = reader
	return {
		admitted: true,
		chats,
		titles: { ...boot.titles },
		titleRevs,
		readers,
		loginUsage: usageOfLogins(boot.setup.myLogins),
		machine: boot.machine,
		setup: boot.setup,
		more: boot.more,
	}
}

/** When a login's usage was taken: the change of its state or its windows' reading, the newer. */
export function usageAt(usage: LoginUsage): number {
	return Math.max(usage.since ?? 0, usage.windows?.at ?? 0)
}

/** The newer of two usages of one login; either may be missing, and a tie keeps the second. */
export function newerUsage(
	a: LoginUsage | undefined,
	b: LoginUsage | undefined,
): LoginUsage | undefined {
	if (a === undefined) return b
	if (b === undefined) return a
	return usageAt(b) >= usageAt(a) ? b : a
}

/** The usage of the logins a read brought (the setup's own logins), by id. */
function usageOfLogins(
	lines: readonly { id: string; usage?: LoginUsage }[] | undefined,
): Record<string, LoginUsage> {
	const out: Record<string, LoginUsage> = {}
	for (const line of lines ?? []) if (line.usage !== undefined) out[line.id] = line.usage
	return out
}

/** The slice with one login's usage, kept only when it is not older than the one it holds. */
export function withLoginUsage(slice: AichatSlice, usage: LoginUsage): AichatSlice {
	const held = slice.loginUsage[usage.login]
	if (newerUsage(held, usage) !== usage) return slice
	return { ...slice, loginUsage: { ...slice.loginUsage, [usage.login]: usage } }
}

/** The slice with the usage a read of the reader's own logins brought, where it is the newer. */
export function withLoginsRead(
	slice: AichatSlice,
	lines: readonly { id: string; usage?: LoginUsage }[] | undefined,
): AichatSlice {
	return Object.values(usageOfLogins(lines)).reduce(withLoginUsage, slice)
}

/**
 * The newest usage the reader knows of one login: the one a read brought with its line (`held`) or
 * the one an event brought since (`heard`, the slice's `loginUsage`), whichever was taken later.
 */
export function newestUsage(
	heard: Readonly<Record<string, LoginUsage>>,
	id: string,
	held: LoginUsage | undefined,
): LoginUsage | undefined {
	return newerUsage(held, heard[id])
}

/** A login line with the newest usage the reader knows of it. */
export function withNewestUsage<T extends { id: string; usage?: LoginUsage }>(
	heard: Readonly<Record<string, LoginUsage>>,
	line: T,
): T {
	const usage = newestUsage(heard, line.id, line.usage)
	return usage === undefined || usage === line.usage ? line : { ...line, usage }
}

/** One chat's row, kept only when it is newer than the one the slice holds. */
export function withRow(slice: AichatSlice, row: ChatRow): AichatSlice {
	const held = slice.chats[row.chatId]
	if (held !== undefined && held.rev >= row.rev) return slice
	return { ...slice, chats: { ...slice.chats, [row.chatId]: row } }
}

/**
 * A chat's row with one preview's line of `aichat.preview.changed` (S07 §5.4): the event carries that
 * line and nothing else, since a start, a stop, a failure or a public switch moves no other fact of
 * the chat, so the row swaps the line of the event's name, in its place, and the chip and the list's
 * words follow without a read. A preview added or removed arrives as a whole new row
 * (`aichat.chat.changed`), in the server's order; a name the row lacks is only added at the end. A
 * line older than the row, or another chat's, changes nothing: whether a preview may run at all
 * changes with the chat's mode and life, which arrive as a whole new row too.
 */
export function withPreviewLine(row: ChatRow, line: PreviewLine): ChatRow {
	if (row.chatId !== line.chatId || line.rev < row.rev) return row
	const held = row.previews ?? []
	const previews = held.some((p) => p.name === line.name)
		? held.map((p) => (p.name === line.name ? line : p))
		: [...held, line]
	return { ...row, previews }
}

/** The slice with one chat's preview line (`withPreviewLine`). */
export function withPreview(slice: AichatSlice, line: PreviewLine): AichatSlice {
	const row = slice.chats[line.chatId]
	if (row === undefined) return slice
	const next = withPreviewLine(row, line)
	return next === row ? slice : { ...slice, chats: { ...slice.chats, [line.chatId]: next } }
}

/** Titles read together with the `titleRev` of the rows they were read beside. */
export function withTitles(
	slice: AichatSlice,
	titles: Record<number, string>,
	rows: readonly ChatRow[],
): AichatSlice {
	const titleRevs = { ...slice.titleRevs }
	for (const row of rows) {
		if (titles[row.chatId] !== undefined) titleRevs[row.chatId] = row.titleRev
	}
	return { ...slice, titles: { ...slice.titles, ...titles }, titleRevs }
}

/** A record without one chat's entry. */
function without<T>(record: Record<number, T>, chatId: number): Record<number, T> {
	const next = { ...record }
	delete next[chatId]
	return next
}

/** The slice without one chat: the reader no longer owns it. */
export function withoutChat(slice: AichatSlice, chatId: number): AichatSlice {
	if (slice.chats[chatId] === undefined && slice.titles[chatId] === undefined) return slice
	return {
		...slice,
		chats: without(slice.chats, chatId),
		titles: without(slice.titles, chatId),
		titleRevs: without(slice.titleRevs, chatId),
		readers: without(slice.readers, chatId),
	}
}

/**
 * The chats whose title the slice read before the row's `titleRev` moved: a rename (a person's, or
 * Claude naming the chat after its first answer) happened since, and the title is read again.
 */
export function staleTitles(slice: AichatSlice): number[] {
	return Object.values(slice.chats)
		.filter((row) => (slice.titleRevs[row.chatId] ?? -1) < row.titleRev)
		.map((row) => row.chatId)
}

/**
 * What of this slice is worth a disk (`FrontendModule.dehydrate`): the rows, their titles, the read
 * state and the form's options, so a second visit draws the start page and the dots before the
 * bootstrap answers. The logins' usage and the machine's line are left out: they are about now, and
 * the bootstrap brings them fresh.
 */
export function dehydrateAichatSlice(slice: AichatSlice): AichatSlice {
	return { ...slice, loginUsage: {}, machine: null }
}

/**
 * Whether a row read back off the disk has the shape this build draws: every row carries its
 * environment's line since containers replaced the dev machine's accounts (S13 §12), and its previews
 * by name, each line saying its kind, since previews were named (S07 §5.4). A row cached before then
 * holds `platforms` or lines without a kind, and is read again rather than drawn.
 */
export function isCurrentRow(row: ChatRow): boolean {
	return (
		typeof row.env === "object" &&
		Array.isArray(row.previews) &&
		row.previews.every((line) => typeof line.name === "string" && typeof line.kind === "string")
	)
}

/**
 * A slice read back off the disk, as a new visit must see it (`FrontendModule.hydrate`): the empty
 * slice with what the cache legitimately holds copied onto it, field by field, so a field added since
 * the cache was written arrives as its empty value and never as `undefined`. A row of an older shape
 * is dropped rather than guarded at every reader, and the slice then says it holds fewer chats than
 * the reader owns (`more`), so the lists ask the server and the watch reads the rows again
 * (`watch.ts`).
 */
export function hydrateAichatSlice(stored: unknown): AichatSlice {
	const slice = AICHAT_SLICE.hydrate(stored)
	const stale = Object.values(slice.chats).filter((row) => !isCurrentRow(row))
	if (stale.length === 0) return slice
	const kept = stale.reduce((held, row) => withoutChat(held, row.chatId), slice)
	return { ...kept, more: true }
}
